import type { Tour } from '../tour-types';

/**
 * Settings, Billing. Which panels exist depends on the account and the viewer: an agency's
 * own view has the agency fee and no credits or plans, only an agency has Clients, Plans
 * and the agency fee are for Owners and Admins of whoever pays, and a client billed through
 * its agency has no invoices or usage report. A step whose panel is not on screen drops
 * out. The panels render at once with their own loading lines, so the tour can start
 * before the figures arrive.
 *
 * There is no billing capability: the panels with billing actions are already hidden from
 * Members, so their steps drop out for them without `requires`.
 */
export const settingsBillingTour: Tour = {
  key: 'settings.billing',
  version: 1,
  routes: ['/settings/billing'],
  steps: [
    {
      anchor: 'billing-plan',
      placement: 'bottom',
      title: 'Your plan',
      body: 'The plan this account is on, its status, and how it is billed. Manage billing opens the payment portal, where Owners and Admins update the card, get receipts or cancel.',
    },
    {
      anchor: 'billing-agency-fee',
      placement: 'bottom',
      title: 'Agency fee',
      body: 'What your agency pays for the agency features, monthly or annual, whatever the number of clients. Each client then gets a plan of its own.',
    },
    {
      anchor: 'billing-usage',
      placement: 'bottom',
      title: 'Usage',
      body: "Sessions your websites have used against the plan this period, and when it resets. The period runs monthly from the plan's start date, not by calendar month.",
    },
    {
      anchor: 'billing-credits',
      title: 'AI credits',
      body: "What AI features can still spend: your plan's credits, which renew each period, then any packs you bought. Owners and Admins buy packs here, valid for 12 months.",
    },
    {
      anchor: 'billing-plans',
      placement: 'top',
      title: 'Plans',
      body: 'Pick a plan to see what changing to it does, what is charged and when. Nothing changes until you accept.',
    },
    {
      anchor: 'billing-clients',
      placement: 'top',
      title: 'Client plans',
      body: 'Each client has a plan of its own. Choose a client to see and change its plan, credits and daily usage.',
    },
    {
      anchor: 'billing-daily',
      placement: 'top',
      title: 'Daily usage',
      body: 'Website sessions per day this calendar month. With a client chosen under Clients, it shows only that client.',
    },
    {
      anchor: 'billing-invoices',
      placement: 'top',
      title: 'Invoices',
      body: 'Every charge on this account: first payment, renewals, plan changes and credit packs. Receipts and PDFs are in the portal under Manage billing.',
    },
    {
      anchor: 'billing-usage-report',
      placement: 'top',
      title: 'Usage report',
      body: "Download a month's sessions as a CSV, itemised by account and website. An agency can use it to show each client what it used.",
    },
  ],
};
